import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedRunInstance } from "../services/runInstance";

interface IUseFetchRunInstance {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: any;
  pSize: any;
  searchTerm?: string;
  statusFilter?: string;
}

const useFetchPaginatedRunInstance = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  statusFilter = "Active",
}: IUseFetchRunInstance) => {
  const [runInstance, setRunInstance] = useState<any>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Map statusFilter to isActive for API call
  const isActive =
    statusFilter === "Active"
      ? "true"
      : statusFilter === "Inactive"
      ? "false"
      : "all";

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedRunInstance({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setRunInstance(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setRunInstance([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [runInstance, fetchData];
};

export default useFetchPaginatedRunInstance;
